import React, { useState } from 'react';
import { adminAPI } from '../../api/admin';
import { isPasswordValid, validatePassword } from '../../utils/passwordValidation';
import UserForm, { UserFormData } from '../forms/UserForm';
import type { PasswordValidation } from '../shared';
import Button from '../ui/Button';
import Modal from '../ui/Modal';

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onError: (message: string) => void;
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({ isOpen, onClose, onSuccess, onError }) => {
  const [formData, setFormData] = useState<UserFormData>({
    username: '',
    password: '',
    confirmPassword: '',
    role: 'user',
  });
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>(validatePassword(''));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handlePasswordChange = (password: string) => {
    setFormData({ ...formData, password });
    setPasswordValidation(validatePassword(password));
  };

  const handleCancel = () => {
    setFormData({
      username: '',
      password: '',
      confirmPassword: '',
      role: 'user',
    });
    setPasswordValidation(validatePassword(''));
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.username.trim()) {
      onError('Username is required');
      return;
    }

    if (!formData.password) {
      onError('Password is required');
      return;
    }

    if (!isPasswordValid(passwordValidation)) {
      onError('Password does not meet the required criteria');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      onError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    try {
      await adminAPI.createUser({
        username: formData.username.trim(),
        password: formData.password,
        role: formData.role!,
      });

      handleCancel(); // Reset form and close modal
      onSuccess();
    } catch (error) {
      console.error('Failed to create user:', error);
      const message = error instanceof Error ? error.message : 'Failed to create user';
      onError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} title="Create New User" maxWidth="max-w-md">
      <form onSubmit={handleSubmit}>
        <UserForm
          formData={formData}
          passwordValidation={passwordValidation}
          onFormChange={setFormData}
          onPasswordChange={handlePasswordChange}
          showRole
          disabled={isSubmitting}
        />

        <div className="mt-6 flex justify-end space-x-3">
          <Button type="button" variant="secondary" onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting} isLoading={isSubmitting}>
            Create User
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateUserModal;
